import {
	SHOPPER,
	audiencesFor,
	isAvailableTo,
	type Viewer
} from "@/domain/catalog/audience";
import type { Audience, OnlineStatus } from "@/domain/catalog/online-status";
import type { ProductType } from "@/domain/catalog/product";

export const LISTING_TYPES: readonly ProductType[] = [
	"durable",
	"consumable",
	"kit",
	"bundle",
	"subscription"
];

export const LISTING_STATUSES: readonly OnlineStatus[] = [
	"active",
	"out-of-stock",
	"temporarily-unavailable",
	"pre-launch"
];

export interface ListingEligibilityFacts {
	onlineStatus: OnlineStatus;
	availableTo: Audience[];
	notSearchable: boolean;
	virtualSku: boolean;
	type: ProductType;
}

const INDEX_HAS_ELIGIBILITY_FACETS = true;

export interface ListingFilterOptions {
	categoryCode: string;
	viewer?: Viewer;
	indexHasEligibilityFacets?: boolean;
}

function quote(value: string): string {
	return `"${value.replace(/"/g, '\\"')}"`;
}

function anyOf(attribute: string, values: readonly string[]): string {
	return `(${values.map((v) => `${attribute}:${quote(v)}`).join(" OR ")})`;
}

export function buildListingFilters({
	categoryCode,
	viewer = SHOPPER,
	indexHasEligibilityFacets = INDEX_HAS_ELIGIBILITY_FACETS
}: ListingFilterOptions): string {
	const clauses = [`categories.code:${quote(categoryCode)}`];

	if (indexHasEligibilityFacets) {
		clauses.push(
			"notSearchable:false",
			"virtualSku:false",
			anyOf("availableTo", audiencesFor(viewer)),
			anyOf("type", LISTING_TYPES),
			anyOf("onlineStatus", LISTING_STATUSES)
		);
	}

	return clauses.join(" AND ");
}

export function isListingEligible(
	product: ListingEligibilityFacts,
	viewer: Viewer = SHOPPER
): boolean {
	if (product.notSearchable) return false;
	if (product.virtualSku) return false;
	if (!LISTING_TYPES.includes(product.type)) return false;
	if (!LISTING_STATUSES.includes(product.onlineStatus)) return false;

	return isAvailableTo(product.availableTo, viewer);
}
